import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './HospitalCard.css';

const BannerUploadSlot = ({ imageSrc, imageAlt, uploading, onSelectFile }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onSelectFile?.(file);
    e.target.value = '';
  };

  return (
    <div className="hospital-card__banner-upload">
      {imageSrc ? (
        <img src={imageSrc} alt={imageAlt} className="hospital-card__banner" />
      ) : (
        <div className="hospital-card__banner-placeholder">
          <span>{uploading ? '…' : 'Banner'}</span>
        </div>
      )}
      <label className="hospital-card__banner-upload-btn">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={handleChange}
          disabled={uploading}
        />
        {imageSrc ? 'Change' : 'Upload'}
      </label>
    </div>
  );
};

const LogoUploadSlot = ({ imageSrc, imageAlt, uploading, onSelectFile }) => {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onSelectFile?.(file);
    e.target.value = '';
  };

  return (
    <div className="hospital-card__logo-upload">
      {imageSrc ? (
        <div className="hospital-card__logo-wrap">
          <img src={imageSrc} alt={imageAlt} className="hospital-card__logo" />
        </div>
      ) : (
        <div className="hospital-card__logo-placeholder">
          <span>{uploading ? '…' : 'Logo'}</span>
        </div>
      )}
      <label className="hospital-card__logo-upload-btn">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="visually-hidden"
          onChange={handleChange}
          disabled={uploading}
        />
        {imageSrc ? 'Change' : 'Upload'}
      </label>
    </div>
  );
};

export const HospitalCard = ({
  slug,
  id,
  banner,
  logo,
  name,
  type,
  address,
  hours,
  website,
  onViewProfile,
  hideActions = false,
  allowImageUpload = false,
  onUploadBanner,
  onUploadLogo,
  uploadingBanner = false,
  uploadingLogo = false,
}) => {
  const navigate = useNavigate();

  const handleViewProfile = (e) => {
    e.preventDefault();
    if (onViewProfile) {
      onViewProfile(id);
      return;
    }
    const pathSlug = slug || id;
    if (pathSlug) navigate(`/hospitals/${pathSlug}`);
  };

  return (
    <div className="hospital-card">
      <div className="hospital-card__banner-wrap">
        {allowImageUpload ? (
          <BannerUploadSlot
            imageSrc={banner}
            imageAlt={name ? `${name} banner` : 'Hospital banner'}
            uploading={uploadingBanner}
            onSelectFile={onUploadBanner}
          />
        ) : (
          <img src={banner} alt={name} className="hospital-card__banner" />
        )}
      </div>

      <div className={`hospital-card__identity${allowImageUpload ? ' hospital-card__identity--upload' : ''}`}>
        {allowImageUpload ? (
          <LogoUploadSlot
            imageSrc={logo}
            imageAlt={name ? `${name} logo` : 'Hospital logo'}
            uploading={uploadingLogo}
            onSelectFile={onUploadLogo}
          />
        ) : (
          <div className="hospital-card__logo-wrap">
            <img src={logo} alt={`${name} logo`} className="hospital-card__logo" />
          </div>
        )}
        <div className="hospital-card__identity-text">
          {name ? <p className="hospital-card__name">{name}</p> : null}
          {type ? <p className="hospital-card__type">{type}</p> : null}
        </div>
      </div>

      <div className="hospital-card__body">
        {address ? (
          <div className="hospital-card__info-row">
            <svg className="hospital-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <span>{address}</span>
          </div>
        ) : null}

        {hours ? (
          <div className="hospital-card__info-row">
            <svg className="hospital-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span>Hours : &nbsp;{hours}</span>
          </div>
        ) : null}

        {website ? (
          <div className="hospital-card__info-row">
            <svg className="hospital-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span>Website: {website}</span>
          </div>
        ) : null}

        {!hideActions && (
          <>
            <Button className="hospital-card__btn-primary w-100 mb-2">
              Book Treatment
            </Button>
            <Button
              variant="outline-secondary"
              className="hospital-card__btn-outline w-100"
              onClick={handleViewProfile}
            >
              View Profile
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
